import { CUSTOMER_STATUSES, CUSTOMER_TYPES } from '@deli/contracts';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  CUSTOMER_PAGE,
  useCreateCustomer,
  useCustomers,
  type CustomerFilter,
} from '../api/customers.js';
import { ErrorText } from '../components/ErrorText.js';
import { Pager } from '../components/Pager.js';
import { QueryState } from '../components/QueryState.js';
import { t } from '../i18n/uk.js';

const tc = t.customers;

export const statusBadge = (status: keyof typeof tc.statuses) => (
  <span className={status === 'active' ? 'badge ok' : 'badge bad'}>{tc.statuses[status]}</span>
);

function CreateCustomerForm() {
  const create = useCreateCustomer();
  const navigate = useNavigate();
  const empty = { type: 'b2c' as const, fullName: '', phone: '', email: '' };
  const [form, setForm] = useState<{
    type: 'b2c' | 'b2b';
    fullName: string;
    phone: string;
    email: string;
  }>(empty);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      { ...form, phone: form.phone || null, email: form.email || null },
      { onSuccess: (card) => void navigate(`/customers/${card.id}`) },
    );
  };
  return (
    <form className="card inline-form" onSubmit={submit}>
      <h2>{tc.add}</h2>
      <select
        aria-label={tc.type}
        value={form.type}
        onChange={(e) => setForm({ ...form, type: e.target.value as 'b2c' | 'b2b' })}
      >
        {CUSTOMER_TYPES.map((type) => (
          <option key={type} value={type}>
            {tc.types[type]}
          </option>
        ))}
      </select>
      <input
        aria-label={tc.name}
        placeholder={tc.name}
        required
        value={form.fullName}
        onChange={(e) => setForm({ ...form, fullName: e.target.value })}
      />
      <input
        aria-label={tc.phone}
        placeholder="050 123 45 67"
        type="tel"
        value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })}
      />
      <input
        aria-label={tc.email}
        placeholder={tc.email}
        type="email"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
      />
      <button type="submit" className="primary" disabled={create.isPending}>
        {tc.create}
      </button>
      <ErrorText error={create.error} />
    </form>
  );
}

export function CustomersPage() {
  const [draft, setDraft] = useState<CustomerFilter>({});
  const [filter, setFilter] = useState<CustomerFilter>({});
  const customers = useCustomers(filter);

  const apply = (event: FormEvent) => {
    event.preventDefault();
    setFilter({ ...draft, search: draft.search?.trim() || undefined, offset: 0 });
  };

  return (
    <section className="wide">
      <h1>{tc.title}</h1>
      <form className="card inline-form" onSubmit={apply}>
        <input
          aria-label={t.common.search}
          className="grow"
          placeholder={tc.searchPlaceholder}
          value={draft.search ?? ''}
          onChange={(e) => setDraft({ ...draft, search: e.target.value })}
        />
        <select
          aria-label={tc.type}
          value={draft.type ?? ''}
          onChange={(e) =>
            setDraft({ ...draft, type: (e.target.value || undefined) as CustomerFilter['type'] })
          }
        >
          <option value="">
            {tc.type}: {t.common.all}
          </option>
          {CUSTOMER_TYPES.map((type) => (
            <option key={type} value={type}>
              {tc.types[type]}
            </option>
          ))}
        </select>
        <select
          aria-label={tc.status}
          value={draft.status ?? ''}
          onChange={(e) =>
            setDraft({
              ...draft,
              status: (e.target.value || undefined) as CustomerFilter['status'],
            })
          }
        >
          <option value="">
            {tc.status}: {t.common.all}
          </option>
          {CUSTOMER_STATUSES.map((status) => (
            <option key={status} value={status}>
              {tc.statuses[status]}
            </option>
          ))}
        </select>
        <button type="submit">{t.common.search}</button>
      </form>
      <QueryState query={customers}>
        {(page) =>
          page.items.length === 0 ? (
            <p className="muted">{tc.empty}</p>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>{tc.name}</th>
                    <th>{tc.type}</th>
                    <th>{tc.company}</th>
                    <th>{tc.phone}</th>
                    <th>{tc.email}</th>
                    <th>{tc.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {page.items.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <Link to={`/customers/${c.id}`}>{c.fullName}</Link>
                      </td>
                      <td>{tc.types[c.type]}</td>
                      <td>{c.companyName ?? '—'}</td>
                      <td>{c.phone ?? '—'}</td>
                      <td>{c.email ?? '—'}</td>
                      <td>{statusBadge(c.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pager
                offset={filter.offset ?? 0}
                pageSize={CUSTOMER_PAGE}
                total={page.total}
                onChange={(offset) => setFilter({ ...filter, offset })}
              />
            </>
          )
        }
      </QueryState>
      <CreateCustomerForm />
    </section>
  );
}
